/**
 * Stats Counter Animation
 * Animates stat numbers from 0 to their final value on scroll
 */
import { runWhenPageReady } from '../utils/PageLifecycle';

class StatsCounter {
  constructor() {
    this.stats = document.querySelectorAll('.stats-feature__stat-number');
    this.animated = new Set();
    this.init();
  }

  init() {
    if (!this.stats.length) return;

    // Create Intersection Observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.animated.has(entry.target)) {
            this.animateNumber(entry.target);
            this.animated.add(entry.target);
          }
        });
      },
      {
        threshold: 0.5, // Trigger when 50% visible
        rootMargin: '0px',
      }
    );

    // Observe each stat number
    this.stats.forEach((stat) => {
      observer.observe(stat);
    });
  }

  animateNumber(element) {
    const finalValue = parseFloat(element.textContent);
    
    // Skip if not a valid number
    if (isNaN(finalValue)) return;

    const duration = 3000; // 3 seconds - smooth and natural
    const startTime = performance.now();
    const startValue = 0;

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Cubic ease-out: smooth deceleration, not too slow at end
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (finalValue - startValue) * easeProgress;
      
      // Format number (keep whole numbers whole, decimals as is)
      const formattedValue = Number.isInteger(finalValue)
        ? Math.round(currentValue)
        : currentValue.toFixed(1);
      
      element.textContent = formattedValue;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        element.textContent = finalValue; // Ensure final value is exact
      }
    };

    requestAnimationFrame(animate);
  }
}

runWhenPageReady(() => new StatsCounter());

export default StatsCounter;
